import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import { Button, Text } from '@revelio/ui';

import type { RowLimitResultsByInstanceId } from '../api/data-builder-row-limit.types';
import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { DatasetSwitcherItem } from '../components/ui/DatasetSwitcherItem';
import {
  selectActiveInstanceId,
  selectConfigByInstance,
  selectDatasetInstances,
  selectModelVersions,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import { getDeliverableColumnConfig } from '../datasets/deliverable-column-config';
import { seedInstanceDefaults } from '../datasets/initial-instance-config';
import { pickLastUsedUoaForTarget } from '../unit-of-analysis-defaulting';
import {
  buildConditionContext,
  filterUnitOptionsByModels,
} from './step2-column/filter-config-by-models';

type Step2DatasetSwitcherProps = {
  instanceIdsWithErrors: Set<string>;
  showValidationErrors: boolean;
  rowLimitResultsByInstanceId: RowLimitResultsByInstanceId;
  pendingInstanceIds: Set<string>;
  apiErrorInstanceIds: Set<string>;
  isWaitingForCompanies: boolean;
};

export const Step2DatasetSwitcher = ({
  instanceIdsWithErrors,
  showValidationErrors,
  rowLimitResultsByInstanceId,
  pendingInstanceIds,
  apiErrorInstanceIds,
  isWaitingForCompanies,
}: Step2DatasetSwitcherProps) => {
  const { data } = useDataBuilderQuery();
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const activeInstanceId = useDataBuilderSelector(selectActiveInstanceId);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const {
    setActiveInstanceId,
    addDatasetInstance,
    removeDatasetInstance,
    renameDatasetInstance,
    setUnitOfAnalysis,
    setUnitOfAnalysisSelections,
    setCompanySelections,
    setCompanySets,
    setCompanyMapper,
  } = useDataBuilderActions();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const lastAutoActivatedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const firstId = instances[0]?.instanceId;
    if (activeInstanceId !== null || firstId === undefined) return;
    if (lastAutoActivatedIdRef.current === firstId) return;
    lastAutoActivatedIdRef.current = firstId;
    setActiveInstanceId(firstId);
  }, [activeInstanceId, instances, setActiveInstanceId]);

  const labelMap = buildDatasetLabelMap(data?.datasets);
  const instanceLabels = buildDatasetInstanceLabelMap(instances, labelMap);
  const items = instances.map((inst) => ({
    id: inst.instanceId,
    label: instanceLabels[inst.instanceId] ?? inst.datasetKey,
  }));

  if (items.length === 0) {
    return null;
  }

  const handleAddDataset = (datasetKey: string) => {
    const existingInstanceIds = instances.map((inst) => inst.instanceId);
    const deliverableConfig = getDeliverableColumnConfig(
      data?.deliverablesConfig ?? {},
      datasetKey
    );
    const defaultSettings = Object.fromEntries(
      (deliverableConfig?.settings ?? []).map((setting) => [
        setting.key,
        setting.default,
      ])
    );
    // Seed file/column defaults at instance-creation time so the duplicated
    // dataset doesn't have to wait on a Step 2 effect to populate them.
    const initialConfig =
      deliverableConfig !== undefined
        ? {
            ...seedInstanceDefaults(deliverableConfig),
            settings: defaultSettings,
          }
        : undefined;
    const newId = addDatasetInstance(datasetKey, initialConfig);

    const conditionContext = buildConditionContext(
      modelVersions,
      null,
      defaultSettings
    );
    const availableValues = filterUnitOptionsByModels(
      deliverableConfig?.unitOfAnalysis.options ?? [],
      conditionContext
    ).map((option) => option.value);
    const pickedUoa = pickLastUsedUoaForTarget({
      configByInstance,
      orderedInstanceIds: existingInstanceIds,
      availableUoaValues: availableValues,
    });
    if (pickedUoa !== null) {
      const sourceUoaSetAt =
        configByInstance[pickedUoa.sourceInstanceId]?.uoaSetAt ?? null;
      setUnitOfAnalysis(newId, pickedUoa.unitOfAnalysis, {
        uoaSetAt: sourceUoaSetAt,
      });
      setUnitOfAnalysisSelections(newId, pickedUoa.unitOfAnalysisSelections);

      if (pickedUoa.unitOfAnalysis === 'company') {
        const sourceConfig = configByInstance[pickedUoa.sourceInstanceId];
        if (sourceConfig !== undefined) {
          if (sourceConfig.companySets !== null) {
            setCompanySets(newId, sourceConfig.companySets);
          } else if (Object.keys(sourceConfig.companySelections).length > 0) {
            setCompanySelections(newId, sourceConfig.companySelections);
          }
          if (sourceConfig.companyMapper !== null) {
            setCompanyMapper(newId, sourceConfig.companyMapper);
          }
        }
      }
    }

    setActiveInstanceId(newId);
    onClose();
  };

  const availableDatasets = (data?.datasets ?? []).filter(
    (ds) => !ds.isDisabled
  );

  return (
    <Box
      data-testid="data-builder-dataset-switcher"
      borderRightWidth="1px"
      borderRightColor="gray.200"
      minWidth="280px"
      maxWidth="320px"
      flexShrink={0}
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box flex={1} minHeight={0} overflow="auto">
        <Box borderBottomWidth="1px" borderBottomColor="gray.200">
          {items.map((item) => (
            <DatasetSwitcherItem
              key={item.id}
              id={item.id}
              label={item.label}
              isActive={item.id === activeInstanceId}
              hasError={
                showValidationErrors && instanceIdsWithErrors.has(item.id)
              }
              rowLimitInfo={rowLimitResultsByInstanceId[item.id]}
              rowLimitLoading={
                pendingInstanceIds.has(item.id) ||
                (isWaitingForCompanies &&
                  rowLimitResultsByInstanceId[item.id] === undefined)
              }
              rowLimitApiError={apiErrorInstanceIds.has(item.id)}
              onClick={() => setActiveInstanceId(item.id)}
              onRename={(name) => renameDatasetInstance(item.id, name)}
              onDelete={() => removeDatasetInstance(item.id)}
            />
          ))}
        </Box>

        <Box padding="2">
          <Popover placement="bottom" isOpen={isOpen} onClose={onClose}>
            <PopoverTrigger>
              <div>
                <Button
                  data-testid="data-builder-add-dataset"
                  color="lightBlue"
                  variant="ghost"
                  size="xs"
                  onClick={onToggle}
                  style={{ width: '100%' }}
                >
                  + Add Dataset
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent width="240px">
              <PopoverBody padding={0} maxHeight="280px" overflow="auto">
                {availableDatasets.map((ds) => (
                  <Flex
                    key={ds.id}
                    data-testid={`data-builder-add-dataset-option-${ds.id}`}
                    padding="8px 12px"
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => handleAddDataset(ds.id)}
                  >
                    <Text fontSize="sm">{ds.label}</Text>
                  </Flex>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </Box>
    </Box>
  );
};
